/**
 * The Election view (prototype `election-page`, Website ADR 0005): one cycle,
 * its confirmed ballots and the Races on the selected ballot.
 *
 * - The Webflow narrative leads, when one exists.
 * - The header states each date's standing; a Ballot dropdown appears with two or more. Selection defaults to the
 *   live ballot; an unknown `ballot` falls back to it rather than 404ing.
 * - With public candidacies the ballot renders as the ledger (state B);
 *   without any, as the pre-candidacy state (A), listing current
 *   officeholders labelled so none reads as a candidate.
 */
import {selectBallotPresentation, shortBallotDate, type SelectedCycleRace} from "../ballot-model";
import {ballotTimeline} from "../components/ballot-timeline";
import {singleSelect} from "../components/dropdown";
import type { ToolContext } from "../context";
import type { PublicCycle, PublicRace } from "../api";
import type { ViewRenderer } from "../context";
import { h } from "../dom";
import { directoryHref } from "../links";
import { buildUrl, parseElectionFilters, parseUrl, type View } from "../router";
import { renderState } from "../components/states";
import { bodyLevel, groupByBody, raceGroupHead, renderRaceSection, renderRaceLedger, raceMatches } from "../components/race-list";
import { getNarrative } from "../webflow-content";
import { openComparison } from "../components/comparison";
import {
  attachDockState,
  renderBallotFinder,
} from "../components/ballot-finder";

type ElectionView = Extract<View, { kind: "election" }>;

function header(cycle: PublicCycle): HTMLElement {
  return h(
    "header",
    { class: "bsc-election__header" },
    h("h1", null, cycle.name),
    ballotTimeline(cycle)
  );
}

/** Below the hero, above the Tool: the same way back the Person view gives. */
function backLink(href: string): HTMLElement {
  return h("a", { class: "bsc-return bsc-election__back", href }, h("span", { "aria-hidden": "true" }, "← "), "All elections");
}

function ballotControl(cycle: PublicCycle, selected: string, view: ElectionView, ctx: ToolContext): HTMLElement | null {
  if (cycle.ballots.length < 2) return null;
  const control = singleSelect({ name: "ballot", label: "Ballot", value: selected,
    options: [...cycle.ballots.map(b=>({value:b.slug,label:`${b.label} · ${shortBallotDate(b.date)}`})), {value:"all",label:"Everyone who ran"}],
    onChange: value => {
      const lookup = value === "all" ? cycle.liveBallot : value;
      const snapshot = ctx.myBallot();
      if (snapshot && snapshot.ballot !== lookup) ctx.invalidateMyBallot();
      const page = ctx.pageUrl();
      const current = parseUrl(page);
      const currentRace = current.kind === "election" ? current.race : null;
      const next = selectBallotPresentation(cycle,value);
      const pre = !cycle.races.some(r=>r.candidacies.length);
      const filters = parseElectionFilters(page);
      const geographicMatch = !snapshot || snapshot.ballot !== lookup || snapshot.raceSlugs.includes(currentRace ?? "");
      const available = geographicMatch && (pre
        ? cycle.races.some(r=>r.slug===currentRace && (value==="all" || r.ballots.includes(value)))
        : next.some(r=>r.slug===currentRace && raceMatches(r,filters)));
      ctx.replace(buildUrl({...view,ballot:value,race:available?currentRace:null},page,filters));
      ctx.rerender("[data-dropdown=ballot]");
    }
  });
  control.classList.add("bsc-race-filters__ballot");
  return control;
}

function preCandidacy(races: PublicRace[], page: URL, cycle: string, selectedRace: string | null): HTMLElement {
  return h(
    "div",
    { class: "bsc-precandidacy" },
    h(
      "div",
      { class: "bsc-precandidacy__notice" },
      h("h2", null, "Check back later for full details!"),
      h(
        "p",
        null,
        "We're working hard to gather information on all the candidates for this election, " +
          "and will publish what we know once petitions are filed. " +
          "Until then, below you can see what offices will be on the ballot and who is currently serving this term."
      )
    ),
    [...groupByBody(races)].map(([body, list]) =>
      h(
        "section",
        { class: "bsc-race-group", "data-level": bodyLevel(list[0]), "aria-label": body },
        raceGroupHead(body, bodyLevel(list[0])),
        list.map((race) => {
          const section = renderRaceSection(race, { page, cycle, stage: "" });
          if (race.slug === selectedRace) {
            section.setAttribute("data-bsc-scroll-target", "");
            section.setAttribute("data-bsc-view-focus", "");
            section.setAttribute("tabindex", "-1");
          }
          return section;
        })
      )
    )
  );
}

/**
 * The required Mapbox credit sits just under the Tool's bottom edge rather than in the
 * docked finder. It is an ordinary block: iOS Safari treats any fixed element near the
 * bottom of the screen as a toolbar and stops the page running behind its address bar.
 */
function placeCredit(tool: HTMLElement, finder: HTMLElement) {
  const credit = finder.querySelector(".bsc-ballot-finder__attribution");
  if (credit) tool.append(credit);
}

/** Race group headings dock under the finder; they need its height to do so. */
function publishFinderHeight(tool: HTMLElement, finder: HTMLElement, signal: AbortSignal) {
  if (typeof ResizeObserver === "undefined" || signal.aborted) return;
  const observer = new ResizeObserver(() => {
    tool.style.setProperty("--bsc-finder-height", `${Math.ceil(finder.getBoundingClientRect().height)}px`);
  });
  observer.observe(finder);
  signal.addEventListener("abort", () => observer.disconnect(), { once: true });
}

export const renderElection: ViewRenderer<ElectionView> = async (region, view, ctx) => {
  const root = h("div", { class: "bsc-election" });
  region.replaceChildren(root);
  // Loading sits in the hero's place, so the canopy, site header and page height
  // match the loaded view instead of collapsing to the footer.
  const loadingHero = h("header", { class: "bsc-election__header bsc-election__header--loading" });
  root.append(loadingHero);
  renderState("loading", loadingHero, { subject: "election", headingLevel: 1 });

  const result = await ctx.api.getCycle(view.cycle);
  if (!result.ok) {
    if (result.reason === "not-found") {
      renderState("not-found", root, { subject: "election", headingLevel: 1, directoryHref: directoryHref(ctx.pageUrl()) });
      return "Election not found";
    }
    renderState("unavailable", root, { subject: "election", headingLevel: 1, onRetry: () => ctx.rerender() });
    return "Election unavailable";
  }

  const cycle = result.data;
  const page = ctx.pageUrl();
  const narrative = getNarrative(cycle.slug);
  const live = cycle.ballots.find(b=>b.slug===cycle.liveBallot) ?? null;
  const ballot = cycle.ballots.find(b=>b.slug===view.ballot) ?? live;
  const selected = view.ballot === "all" ? "all" : ballot?.slug ?? null;
  const ballotParam = view.ballot === "all" ? "all" : ballot && view.ballot === ballot.slug ? ballot.slug : null;
  if (view.ballot && !ballotParam) ctx.replace(buildUrl({...view,ballot:null},page,parseElectionFilters(page)));

  root.replaceChildren(
    ...[
      header(cycle),
      backLink(ctx.directoryReturn()?.href ?? directoryHref(page)),
      narrative ? h("div", { class: "bsc-election__narrative" }, narrative) : null,

    ].filter((n): n is HTMLElement => n !== null)
  );

  const tool = h("div", { class: "bsc-election__tool" });
  root.append(tool);
  if (!ballot || !selected || cycle.races.length === 0) {
    renderState("empty", tool, { subject: "election" });
    return cycle.name;
  }
  const snapshot = ctx.myBallot();
  const myBallot =
    snapshot && snapshot.cycle === cycle.slug && snapshot.ballot === ballot.slug
      ? snapshot
      : null;
  const pre = !cycle.races.some(r=>r.candidacies.length);
  const presentations = selectBallotPresentation(cycle,selected);
  const allRaces = pre ? presentations.filter(r=>selected==="all" || cycle.races.find(c=>c.slug===r.slug)?.ballots.includes(selected)) : presentations;
  const races = myBallot ? allRaces.filter(r=>myBallot.raceSlugs.includes(r.slug)) : allRaces;
  const control = ballotControl(cycle,selected,view,ctx);
  const filters = parseElectionFilters(page);
  const finder = renderBallotFinder({
    mapbox: ctx.mapbox,
    api: ctx.api,
    cycle: cycle.slug,
    ballot: ballot.slug,
    snapshot: myBallot,
    signal: ctx.viewSignal(),
    onMatched: ({ choice, raceSlugs }) =>
      ctx.enterMyBallot({
        cycle: cycle.slug,
        ballot: ballot.slug,
        displayLabel: choice.label,
        point: { latitude: choice.latitude, longitude: choice.longitude },
        raceSlugs,
        priorFilters: filters,
        priorRace: view.race,
        priorScroll: window.scrollY,
        finderFocusTarget: "input",
      }),
    onReturn: () => ctx.leaveMyBallot(),
  });
  tool.append(finder);
  attachDockState(finder, ctx.viewSignal());
  publishFinderHeight(tool, finder, ctx.viewSignal());
  if (pre) {
    tool.classList.add("bsc-election__tool--precandidacy");
    if (control) tool.append(h("div",{class:"bsc-race-filters",role:"group","aria-label":"Filter Races"},control));
    tool.append(preCandidacy(races, page, cycle.slug, view.race));
    placeCredit(tool, finder);
    return cycle.name;
  }

  const { nav, main } = renderRaceLedger(races, {
    page,
    signal: ctx.viewSignal(),
    cycle: cycle.slug,
    ballot: { slug: ballot.slug, stage: ballot.stage },
    ballotParam,
    selectedRace: races.some((race) => race.slug === view.race) ? view.race : null,
    filters,
    showFilters: !myBallot,
    ballotControl: control,
    replace: (href) => ctx.replace(href),
    onCompare: (race, trigger) =>
      openComparison(region, trigger, race.name, { api: ctx.api, page: ctx.pageUrl(), cycle: cycle.slug, race: race.slug, candidateKeys: (race as SelectedCycleRace).candidates.map(c=>c.key) }),
  });
  tool.append(nav, main);
  attachDockState(nav, ctx.viewSignal());
  placeCredit(tool, finder);
  return cycle.name;
};
